import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { LicenseAwareLink } from './LicenseAwareLink';
import { RootState } from '../store/store';
import attention from '../assets/icons/Icon-Alert-Attention.svg';
import React, {FC, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import { addNotification } from '../store/slices/notificationSlice';
import { useDispatch } from 'react-redux';
import { isDataSourceConfigured } from '../utils/dataSourceHelpers';

type FeatureCardProps = {
  title: string,
  description: string,
  illustration: string,
  pluginName: string,
  to: string,
  isComingSoon: boolean
  isBuyWithoutFilling?: boolean
  illustrationScale?: number
  // TODO: remove once the plugin's license product exists in the backend
  skipLicenseCheck?: boolean
}

const FeatureCard: FC<FeatureCardProps> = ({
  title,
  description,
  illustration,
  pluginName,
  to,
  isComingSoon,
  isBuyWithoutFilling,
  illustrationScale = 1,
  skipLicenseCheck = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contact = useSelector((state: RootState) => state.profile.contact);
  const avv = useSelector((state: RootState) => state.profile.avv);
  const dataSourceConfig = useSelector((state: RootState) => state.dataSource.data);
  const profile = useSelector((state: RootState) => state.profile);
  const dispatch = useDispatch();

  const isProfileComplete = useMemo(() => {
    return !!(
      contact?.email &&
      contact?.firstName &&
      contact?.lastName &&
      contact?.country &&
      contact?.city &&
      contact?.street &&
      contact?.houseNumber &&
      contact?.postalCode
    );
  }, [contact]);

  const isAvvAccepted = useMemo(() => avv.isAccepted, [avv]);
  
  // Shared gate: for a bridge DataSource the connection fields are empty by
  // design, so the plain host/database check would lock bridge users out.
  const isDataSourceComplete = useMemo(
    () => isDataSourceConfigured(dataSourceConfig),
    [dataSourceConfig],
  );

  const isAllComplete = useMemo(() =>
    {
      if (profile?.contact?.parentUserId) {
        return isProfileComplete
        && isAvvAccepted
      }
      if (isBuyWithoutFilling) {
        return isProfileComplete
        && isAvvAccepted
      } else {
        return isProfileComplete
        && isAvvAccepted
        && isDataSourceComplete
      }
    },
    [isProfileComplete, isAvvAccepted, isDataSourceComplete, profile]
  );

  const isPurchasable = isProfileComplete && isAvvAccepted;

  const getErrorText = () => {
    if (!isProfileComplete) {
      return t('feature_card_error_profile');
    }
    if (!isAvvAccepted) {
      return t('feature_card_error_avv');
    }
    if (!isDataSourceComplete) {
      return t('feature_card_error_database');
    }
    return null;
  }

  const goToFillData = () => {
    if (!isProfileComplete) {
      navigate('/profile?direction=profile');
      return;
    }
    if (!isAvvAccepted) {
      navigate('/profile?direction=avv');
      return;
    }
    if (!isDataSourceComplete) {
      navigate('/profile?direction=dataSource');
      return;
    }
  }

  return (
    <div
      className="feature-card"
      style={
        illustrationScale !== 1
          ? ({ '--feature-card-illustration-size': `${70 * illustrationScale}px` } as React.CSSProperties)
          : undefined
      }
    >
      <div className="feature-content">
        <div className="feature-card-top">
          <div className="feature-card-title">
            <span>{title}</span>
            <div className="negative-corner"></div>
          </div>
          <div className="feature-card-actions">
            {isPurchasable ? (
              <LicenseAwareLink 
                pluginName={pluginName} 
                to={to}
                parentUserId={profile?.contact?.parentUserId}
                skipLicenseCheck={skipLicenseCheck}
                buyButton={
                  isComingSoon ? 
                    <button className="btn btn-primary" disabled={true}>{t('feature_card_soon')}</button> :
                    <button className="btn btn-primary">{t('feature_card_buy')} <span className="arrow">❯</span></button>
                }
              >
                <button className="btn btn-primary">{t('feature_card_configure')}</button>
              </LicenseAwareLink>
            ) : (
              isComingSoon ?
                <button className="btn btn-primary" disabled={true}>{t('feature_card_soon')}</button> : 
                <button className="btn btn-primary" onClick={goToFillData}>{t('feature_card_complete_profile')}</button>
            )}
          </div>
        </div>

        <div className="feature-card-bottom">
          <p className="feature-card-description">{description}</p>
          <div className="feature-illustration">
            <img src={illustration} alt={title} />
          </div>
          {!isAllComplete &&
            <div className="feature-card-error">
              <div className="feature-card-error-icon">
                <img src={attention} alt={t('feature_card_attention_alt')} />
              </div>
              <div className="feature-card-error-text" onClick={goToFillData}>
                <span>{getErrorText()}</span>
              </div>
            </div>
          }
        </div>

      </div>
    </div>
  );
};

export default FeatureCard;